/**
 * v4.34.10 — Help Center + Investigation Walkthrough
 *
 * Single module that exports two surfaces sharing the same content
 * source so the walkthrough and the searchable help center never
 * drift:
 *
 *  - <HelpCenterDialog />   — searchable accordion knowledge base
 *  - <InvestigationTour />  — 10-step assisted walkthrough overlay
 *
 * Both are dismissible and restartable from the sidebar Help menu.
 * Content is operator-facing; no internal mechanics are exposed.
 */
import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    BookOpen, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
    X, ShieldCheck, FileText, Mail, Sparkles, Compass, CheckCircle,
} from 'lucide-react';

// ─────────────────────────── content source ───────────────────────────
// Keys are i18n keys; each article belongs to a category. Categories
// render as collapsible sections in the dialog.

const HELP_ARTICLES = [
    { id: 'preferredTabs', category: 'gettingStarted', titleKey: 'helpCenter.art.preferredTabs.title', bodyKey: 'helpCenter.art.preferredTabs.body' },
    { id: 'whatIs',       category: 'redemption',  titleKey: 'helpCenter.art.whatIs.title',       bodyKey: 'helpCenter.art.whatIs.body' },
    { id: 'attribution',  category: 'redemption',  titleKey: 'helpCenter.art.attribution.title',  bodyKey: 'helpCenter.art.attribution.body' },
    { id: 'filter',       category: 'redemption',  titleKey: 'helpCenter.art.filter.title',       bodyKey: 'helpCenter.art.filter.body' },
    { id: 'snapshot',     category: 'redemption',  titleKey: 'helpCenter.art.snapshot.title',     bodyKey: 'helpCenter.art.snapshot.body' },
    { id: 'flags',        category: 'redemption',  titleKey: 'helpCenter.art.flags.title',        bodyKey: 'helpCenter.art.flags.body' },
    { id: 'workflow',     category: 'redemption',  titleKey: 'helpCenter.art.workflow.title',     bodyKey: 'helpCenter.art.workflow.body' },
    { id: 'deepLink',     category: 'redemption',  titleKey: 'helpCenter.art.deepLink.title',     bodyKey: 'helpCenter.art.deepLink.body' },
    { id: 'mobile',       category: 'redemption',  titleKey: 'helpCenter.art.mobile.title',       bodyKey: 'helpCenter.art.mobile.body' },
    { id: 'exports',      category: 'exports',     titleKey: 'helpCenter.art.exports.title',      bodyKey: 'helpCenter.art.exports.body' },
    { id: 'handling',     category: 'exports',     titleKey: 'helpCenter.art.handling.title',     bodyKey: 'helpCenter.art.handling.body' },
];

const HELP_FAQS = [
    { id: 'preferredTabs', qKey: 'helpCenter.faq.preferredTabs.q', aKey: 'helpCenter.faq.preferredTabs.a' },
    { id: 'edit',     qKey: 'helpCenter.faq.edit.q',     aKey: 'helpCenter.faq.edit.a' },
    { id: 'scope',    qKey: 'helpCenter.faq.scope.q',    aKey: 'helpCenter.faq.scope.a' },
    { id: 'deleted',  qKey: 'helpCenter.faq.deleted.q',  aKey: 'helpCenter.faq.deleted.a' },
    { id: 'live',     qKey: 'helpCenter.faq.live.q',     aKey: 'helpCenter.faq.live.a' },
    { id: 'restart',  qKey: 'helpCenter.faq.restart.q',  aKey: 'helpCenter.faq.restart.a' },
];

const CATEGORY_ORDER = ['gettingStarted', 'redemption', 'exports', 'faq'];
const CATEGORY_LABEL_KEY = {
    gettingStarted: 'helpCenter.category.gettingStarted',
    redemption: 'helpCenter.category.redemption',
    exports: 'helpCenter.category.exports',
    faq: 'helpCenter.category.faq',
};

// ─────────────────────────── walkthrough context ───────────────────────────

const InvestigationTourContext = createContext(null);

export const useInvestigationTour = () => {
    const ctx = useContext(InvestigationTourContext);
    if (!ctx) throw new Error('useInvestigationTour must be used within InvestigationTourProvider');
    return ctx;
};

export const InvestigationTourProvider = ({ children }) => {
    const { user } = useAuth();
    const [active, setActive] = useState(false);
    const [step, setStep] = useState(0);
    const [helpCenterOpen, setHelpCenterOpen] = useState(false);

    const tourKey = useMemo(
        () => (user?.id ? `yulontap_inv_tour_seen_${user.id}` : null),
        [user?.id],
    );

    const start = useCallback(() => {
        setStep(0);
        setActive(true);
    }, []);

    const dismiss = useCallback(() => {
        setActive(false);
        setStep(0);
        if (tourKey) {
            try { localStorage.setItem(tourKey, 'true'); } catch { /* storage unavailable */ }
        }
    }, [tourKey]);

    const next = useCallback(() => setStep((s) => s + 1), []);
    const prev = useCallback(() => setStep((s) => Math.max(0, s - 1)), []);

    const openHelpCenter = useCallback(() => setHelpCenterOpen(true), []);
    const closeHelpCenter = useCallback(() => setHelpCenterOpen(false), []);

    const value = useMemo(() => ({
        active, step, start, dismiss, next, prev,
        helpCenterOpen, openHelpCenter, closeHelpCenter,
    }), [active, step, start, dismiss, next, prev,
        helpCenterOpen, openHelpCenter, closeHelpCenter]);

    return (
        <InvestigationTourContext.Provider value={value}>
            {children}
            <InvestigationTour />
            <HelpCenterDialog />
        </InvestigationTourContext.Provider>
    );
};

// ─────────────────────────── tour steps ───────────────────────────

const TOUR_STEPS = [
    { id: 'welcome',     titleKey: 'invTour.welcome.title',     bodyKey: 'invTour.welcome.body',     icon: Sparkles },
    { id: 'session',     titleKey: 'invTour.session.title',     bodyKey: 'invTour.session.body',     icon: ShieldCheck },
    { id: 'stats',       titleKey: 'invTour.stats.title',       bodyKey: 'invTour.stats.body',       icon: Compass },
    { id: 'filters',     titleKey: 'invTour.filters.title',     bodyKey: 'invTour.filters.body',     icon: Search },
    { id: 'table',       titleKey: 'invTour.table.title',       bodyKey: 'invTour.table.body',       icon: BookOpen },
    { id: 'flags',       titleKey: 'invTour.flags.title',       bodyKey: 'invTour.flags.body',       icon: Compass },
    { id: 'export',      titleKey: 'invTour.export.title',      bodyKey: 'invTour.export.body',      icon: FileText },
    { id: 'responsible', titleKey: 'invTour.responsible.title', bodyKey: 'invTour.responsible.body', icon: ShieldCheck },
    { id: 'done',        titleKey: 'invTour.done.title',        bodyKey: 'invTour.done.body',        icon: CheckCircle },
];

function InvestigationTour() {
    const { t } = useTranslation();
    const { active, step, next, prev, dismiss } = useInvestigationTour();
    if (!active) return null;
    const current = TOUR_STEPS[step] || TOUR_STEPS[0];
    const isFirst = step === 0;
    const isLast = step >= TOUR_STEPS.length - 1;
    const Icon = current.icon;

    return (
        <Dialog open={active} onOpenChange={(o) => !o && dismiss()}>
            <DialogContent className="sm:max-w-lg p-0 overflow-hidden" data-testid="inv-tour-dialog">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 text-white">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                                <Icon className="w-5 h-5" />
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-300">
                                    {t('invTour.title')}
                                </div>
                                <DialogTitle className="text-white text-lg mt-0.5" data-testid="inv-tour-title">
                                    {t(current.titleKey)}
                                </DialogTitle>
                            </div>
                        </div>
                        <button
                            onClick={dismiss}
                            className="text-slate-300 hover:text-white"
                            aria-label={t('invTour.skip')}
                            data-testid="inv-tour-close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="p-6">
                    <DialogDescription className="text-sm leading-relaxed text-foreground" data-testid="inv-tour-body">
                        {t(current.bodyKey)}
                    </DialogDescription>

                    <div className="flex justify-center gap-1.5 mt-5">
                        {TOUR_STEPS.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === step ? 'w-5 bg-rose-500' :
                                    i < step ? 'w-2 bg-emerald-500' :
                                    'w-2 bg-slate-300 dark:bg-slate-600'
                                }`}
                            />
                        ))}
                    </div>

                    <div className="flex items-center justify-between mt-6">
                        <div className="text-xs text-muted-foreground">
                            {t('invTour.step', { current: step + 1, total: TOUR_STEPS.length })}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isFirst && (
                                <Button variant="ghost" size="sm" onClick={prev} data-testid="inv-tour-prev">
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    {t('invTour.back')}
                                </Button>
                            )}
                            {!isLast && (
                                <Button variant="ghost" size="sm" onClick={dismiss} data-testid="inv-tour-skip">
                                    {t('invTour.skip')}
                                </Button>
                            )}
                            <Button
                                size="sm"
                                onClick={isLast ? dismiss : next}
                                data-testid={isLast ? 'inv-tour-finish' : 'inv-tour-next'}
                            >
                                {isLast ? t('invTour.finish') : t('invTour.next')}
                                {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
                                {isLast && <CheckCircle className="w-4 h-4 ml-1" />}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ─────────────────────────── help center dialog ───────────────────────────

function ArticleAccordion({ titleKey, bodyKey, expanded, onToggle, t, testid }) {
    return (
        <div className="border-b border-border last:border-b-0" data-testid={testid}>
            <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/40 text-left"
                onClick={onToggle}
            >
                <span className="text-sm font-medium">{t(titleKey)}</span>
                {expanded ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
            </button>
            {expanded && (
                <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                    {t(bodyKey)}
                </div>
            )}
        </div>
    );
}

function HelpCenterDialog() {
    const { t, i18n } = useTranslation();
    const { helpCenterOpen, closeHelpCenter, start } = useInvestigationTour();
    const [query, setQuery] = useState('');
    const [opened, setOpened] = useState({});

    // Reset search + accordion state when reopening.
    useEffect(() => {
        if (!helpCenterOpen) {
            setQuery('');
            setOpened({});
        }
    }, [helpCenterOpen]);

    const normalized = (s) => (s || '').toLowerCase();
    const articles = useMemo(() => {
        if (!query) return HELP_ARTICLES;
        const q = normalized(query);
        return HELP_ARTICLES.filter((a) =>
            normalized(t(a.titleKey)).includes(q) || normalized(t(a.bodyKey)).includes(q),
        );
    }, [query, t, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    const faqs = useMemo(() => {
        if (!query) return HELP_FAQS;
        const q = normalized(query);
        return HELP_FAQS.filter((f) =>
            normalized(t(f.qKey)).includes(q) || normalized(t(f.aKey)).includes(q),
        );
    }, [query, t, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    const grouped = useMemo(() => {
        const out = {};
        articles.forEach((a) => {
            (out[a.category] = out[a.category] || []).push(a);
        });
        return out;
    }, [articles]);

    const emptyResults = articles.length === 0 && faqs.length === 0;

    return (
        <Dialog open={helpCenterOpen} onOpenChange={(o) => !o && closeHelpCenter()}>
            <DialogContent
                className="sm:max-w-2xl max-h-[90vh] p-0 overflow-hidden grid grid-rows-[auto_auto_1fr_auto]"
                data-testid="help-center-dialog"
            >
                <DialogHeader className="px-5 pt-5">
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-rose-600" />
                        <DialogTitle data-testid="help-center-title">{t('helpCenter.title')}</DialogTitle>
                    </div>
                    <DialogDescription>{t('helpCenter.subtitle')}</DialogDescription>
                </DialogHeader>

                <div className="px-5 pb-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('helpCenter.search.placeholder')}
                            className="pl-9"
                            data-testid="help-center-search"
                        />
                    </div>
                </div>

                <div className="overflow-y-auto px-5 pb-5">
                    {emptyResults ? (
                        <div className="py-12 text-center text-sm text-muted-foreground" data-testid="help-center-empty">
                            {t('helpCenter.search.noResults')}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {CATEGORY_ORDER.map((cat) => {
                                if (cat === 'faq') {
                                    if (faqs.length === 0) return null;
                                    return (
                                        <section key={cat} data-testid={`help-cat-${cat}`}>
                                            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                                                {t(CATEGORY_LABEL_KEY[cat])}
                                            </h3>
                                            <div className="rounded-lg border border-border bg-card">
                                                {faqs.map((f) => (
                                                    <ArticleAccordion
                                                        key={f.id}
                                                        titleKey={f.qKey}
                                                        bodyKey={f.aKey}
                                                        expanded={!!opened[`faq-${f.id}`]}
                                                        onToggle={() =>
                                                            setOpened({ ...opened, [`faq-${f.id}`]: !opened[`faq-${f.id}`] })
                                                        }
                                                        t={t}
                                                        testid={`help-faq-${f.id}`}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    );
                                }
                                const list = grouped[cat] || [];
                                if (list.length === 0) return null;
                                return (
                                    <section key={cat} data-testid={`help-cat-${cat}`}>
                                        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                                            {t(CATEGORY_LABEL_KEY[cat])}
                                        </h3>
                                        <div className="rounded-lg border border-border bg-card">
                                            {list.map((a) => (
                                                <ArticleAccordion
                                                    key={a.id}
                                                    titleKey={a.titleKey}
                                                    bodyKey={a.bodyKey}
                                                    expanded={!!opened[a.id]}
                                                    onToggle={() => setOpened({ ...opened, [a.id]: !opened[a.id] })}
                                                    t={t}
                                                    testid={`help-article-${a.id}`}
                                                />
                                            ))}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="border-t border-border bg-muted/30 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
                    <Button
                        variant="default"
                        size="sm"
                        onClick={() => { closeHelpCenter(); start(); }}
                        data-testid="help-center-start-inv-tour"
                    >
                        <Compass className="w-4 h-4 mr-1.5" />
                        {t('helpCenter.action.startInvTour')}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        asChild
                        data-testid="help-center-open-manual"
                    >
                        <a href="/api/docs/operational-manual/pdf" target="_blank" rel="noreferrer">
                            <FileText className="w-4 h-4 mr-1.5" />
                            {t('helpCenter.action.openManual')}
                        </a>
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        data-testid="help-center-contact-support"
                    >
                        <a href="mailto:support@yulontap.com">
                            <Mail className="w-4 h-4 mr-1.5" />
                            {t('helpCenter.action.contactSupport')}
                        </a>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
